/**
 * PartnerInquiry — public partnership / regional-pilot inquiry form.
 * No auth. Reuses Sergeant TAP public styling. Content title/intro/success
 * message are admin-editable via homepage-content. Posts to
 * /api/partnership-inquiry (Mongo-first, best-effort email server-side).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { PublicFooter } from '@/components/PublicFooter';
import { PUBLIC_CONTAINER } from '@/components/PublicContainer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORG_TYPES = [
  'Economic development organization',
  'Chamber of commerce',
  'Workforce organization',
  'Employer',
  'Government organization',
  'Military or installation-related organization',
  'Nonprofit or community partner',
  'Other',
];
const INTERESTS = [
  'Regional pilot',
  'Employer participation',
  'Workforce partnership',
  'Military-community partnership',
  'Strategic partnership',
  'Other',
];

// Map untrusted query param -> context label + submitted source value.
function resolveContext(sourceParam) {
  if (sourceParam === 'employer') return { label: 'Employer Participation', source: 'employer_cta' };
  if (sourceParam === 'community') return { label: 'Regional Pilot', source: 'community_cta' };
  if (sourceParam === 'about') return { label: 'Partnership Inquiry', source: 'about' };
  return { label: 'Partnership Inquiry', source: 'public' };
}

const EMPTY = {
  full_name: '', work_email: '', phone: '', organization_name: '',
  organization_type: '', location: '', area_of_interest: '', message: '', website: '',
};

function PartnerInquiry() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const context = resolveContext(params.get('source'));

  const [content, setContent] = useState({
    partner_form_title: 'Partner With Sergeant TAP',
    partner_form_intro: 'Tell us about your organization and what you are trying to accomplish. We will review your inquiry and follow up directly.',
    partner_form_success_message: 'Thank you. Your inquiry has been received, and we will follow up directly.',
  });
  const [form, setForm] = useState({ ...EMPTY, area_of_interest: context.source === 'community_cta' ? 'Regional pilot' : (context.source === 'employer_cta' ? 'Employer participation' : '') });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/homepage-content`).then((r) => {
      const d = r.data || {};
      setContent((prev) => ({
        partner_form_title: d.partner_form_title || prev.partner_form_title,
        partner_form_intro: d.partner_form_intro || prev.partner_form_intro,
        partner_form_success_message: d.partner_form_success_message || prev.partner_form_success_message,
      }));
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const next = {};
    const t = (v) => (v || '').trim();
    if (t(form.full_name).length < 2) next.full_name = 'Please enter your full name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t(form.work_email))) next.work_email = 'Please enter a valid work email.';
    if (t(form.organization_name).length < 2) next.organization_name = 'Please enter your organization name.';
    if (!ORG_TYPES.includes(form.organization_type)) next.organization_type = 'Please select an organization type.';
    if (t(form.location).length < 2) next.location = 'Please enter your city, region, or state.';
    if (!INTERESTS.includes(form.area_of_interest)) next.area_of_interest = 'Please select an area of interest.';
    if (t(form.message).length < 20) next.message = 'Please enter a message of at least 20 characters.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/partnership-inquiry`, { ...form, source: context.source });
      setDone(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setServerError('Too many requests. Please try again later.');
      } else if (err.response?.status === 400 && err.response?.data?.detail) {
        setServerError(err.response.data.detail);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="partner-page">
      <header className="w-full bg-white border-b border-[#e5e5e5]">
        <div className={`${PUBLIC_CONTAINER} flex items-center justify-between h-16`}>
          <a href="/" className="flex items-center gap-2.5" data-testid="partner-logo-link">
            <img src={`${process.env.PUBLIC_URL}/sergeant-tap-logo.png`} alt="Sergeant TAP" className="h-9 w-auto object-contain" />
          </a>
          <button
            data-testid="partner-back-home"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#444] hover:text-brand"
          >
            <ArrowLeft className="w-4 h-4" /> Back to homepage
          </button>
        </div>
      </header>

      <main className={`${PUBLIC_CONTAINER} py-12`}>
        <div className="max-w-2xl">
        <p data-testid="partner-context-label" className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">
          {context.label}
        </p>
        <h1 data-testid="partner-title" className="text-2xl sm:text-3xl font-bold text-[#222] mb-3">
          {content.partner_form_title}
        </h1>
        <p data-testid="partner-intro" className="text-[#555] leading-relaxed mb-8">
          {content.partner_form_intro}
        </p>

        {done ? (
          <div data-testid="partner-success" className="flex items-start gap-3 p-5 rounded-xl bg-brand-light border border-brand/20">
            <CheckCircle2 className="w-6 h-6 text-brand flex-shrink-0 mt-0.5" />
            <p className="text-[#222] leading-relaxed">{content.partner_form_success_message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-5" data-testid="partner-form">
            {serverError && (
              <div data-testid="partner-server-error" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                {serverError}
              </div>
            )}

            {/* Honeypot — visually hidden, not for real users */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={set('website')}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              data-testid="partner-website-honeypot"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />

            <Field label="Full name" required error={errors.full_name}>
              <input data-testid="partner-full-name" className="pf-input" value={form.full_name} onChange={set('full_name')} maxLength={100} />
            </Field>
            <Field label="Work email" required error={errors.work_email}>
              <input data-testid="partner-work-email" type="email" className="pf-input" value={form.work_email} onChange={set('work_email')} maxLength={254} />
            </Field>
            <Field label="Phone (optional)" error={errors.phone}>
              <input data-testid="partner-phone" className="pf-input" value={form.phone} onChange={set('phone')} maxLength={30} />
            </Field>
            <Field label="Organization name" required error={errors.organization_name}>
              <input data-testid="partner-org-name" className="pf-input" value={form.organization_name} onChange={set('organization_name')} maxLength={150} />
            </Field>
            <Field label="Organization type" required error={errors.organization_type}>
              <select data-testid="partner-org-type" className="pf-input" value={form.organization_type} onChange={set('organization_type')}>
                <option value="">Select…</option>
                {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="City, region, or state" required error={errors.location}>
              <input data-testid="partner-location" className="pf-input" value={form.location} onChange={set('location')} maxLength={150} />
            </Field>
            <Field label="Area of interest" required error={errors.area_of_interest}>
              <select data-testid="partner-interest" className="pf-input" value={form.area_of_interest} onChange={set('area_of_interest')}>
                <option value="">Select…</option>
                {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Message" required error={errors.message}>
              <textarea data-testid="partner-message" className="pf-input" rows={5} value={form.message} onChange={set('message')} maxLength={2000} />
            </Field>

            <button
              type="submit"
              data-testid="partner-submit"
              disabled={submitting}
              className="btn-uppercase bg-brand hover:bg-brand-hover text-white px-8 py-3.5 rounded transition-colors text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Inquiry
            </button>
          </form>
        )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#333] mb-1.5">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default PartnerInquiry;
